import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Button, ButtonVariantOption } from "@/components/Button";
import { formatNumberWithComma } from "@/utils/formatNumber";
import { type TransferSelectionSummary } from "@/features/salesforceIntegration/utils/summarizeTransferSelection";
import { type TransferActionState } from "@/features/salesforceIntegration/utils/resolveTransferActionState";
import { excludedReasonLabel } from "@/features/salesforceIntegration/utils/excludedReasonLabel";

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  summary: TransferSelectionSummary;
  /**
   * 操作メニューと同じ送信可否。ダイアログを開いている間に判定結果が変わることがあるため、
   * 確定ボタンにも最新の状態を反映する。
   */
  actionState: TransferActionState;
}

/**
 * Salesforce連携の確認ダイアログ。
 *
 * 選択した行数と送信する物件数を並べて示す。両者は一致しないことがあり、
 * 行数だけを見せると送信件数と食い違うためである。
 * 送らない物件は理由ごとに数える。
 */
export const SalesforceTransferConfirmDialog: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  summary,
  actionState,
}) => {
  const targetCount = summary.targetUniqueIds.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="salesforce-transfer-confirm-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="salesforce-transfer-confirm-dialog-title">
        Salesforceへ連携
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          {`選択した ${formatNumberWithComma(
            summary.selectedRowCount
          )} 行のうち、${formatNumberWithComma(
            targetCount
          )} 物件を連携します。`}
        </Typography>

        {summary.excludedReasons.length > 0 && (
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>内訳</TableCell>
                <TableCell align="right">物件数</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>連携する</TableCell>
                <TableCell align="right">
                  {formatNumberWithComma(targetCount)}
                </TableCell>
              </TableRow>
              {summary.excludedReasons.map((excluded) => (
                <TableRow key={excluded.reasonCode ?? excluded.reasonMessage}>
                  <TableCell>
                    {`除外（${excludedReasonLabel(
                      excluded.reasonCode,
                      excluded.reasonMessage
                    )}）`}
                  </TableCell>
                  <TableCell align="right">
                    {formatNumberWithComma(excluded.count)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Alert severity="info">
          同じ所在・地番でも、取得した経緯が異なる登記は別々に数えます。そのため、選択した行数と連携する物件数は一致しないことがあります。連携済みのものは二重に送りません。
        </Alert>

        {actionState.isDisabled && actionState.tooltipMessage !== "" && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {actionState.tooltipMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", mb: 1 }}>
        <Button
          label="キャンセル"
          onClick={onClose}
          variant={ButtonVariantOption.Outlined}
        />
        <Button
          label="連携する"
          onClick={onConfirm}
          variant={ButtonVariantOption.Contained}
          disabled={actionState.isDisabled}
        />
      </DialogActions>
    </Dialog>
  );
};
